import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { SplugParametersConfigurationRemoteControllerService } from "core/api";
import { ISplugParametersConfigurationDto, LSplug, SPLUGParameters } from "core/dto";
import { RemoteCollectionService, RemoteCollectionStore } from "core/remotes";
import { LoaderService, SnackbarService } from "core/services";
import { SplugConfigService } from "core/splug";
import { Subject, filter, takeUntil, tap } from "rxjs";
import { DsgDeepTranslationKey } from "../../../pipes/translate.pipe";
import { SignProcessWizardService } from "../sign-process-wizard.service";
import applyEntityLoader from "./utils/apply-entity-loader";


export interface ISplugParametersConfiguration<TSplug extends LSplug = LSplug> {
  id?: string,
  actorId?: string,
  userId?: string,
  sigTypeId: string,
  sigTypeParam: SPLUGParameters<TSplug>
}

const splugParametersConfigurationEntityLoader = applyEntityLoader('splug-parameters-cofinguration');

const initRemoteStore = (processId: string, splugParametersConfigurationRemoteControllerService: SplugParametersConfigurationRemoteControllerService, loaderService: LoaderService<DsgDeepTranslationKey>) =>
  new RemoteCollectionStore<ISplugParametersConfigurationDto, string>({
    keyField: 'id',
    byKey: (id) => splugParametersConfigurationEntityLoader({
      obs: splugParametersConfigurationRemoteControllerService.getOne(id),
      loaderService,
      methodKey: 'by-key'
    }),
    load: () => splugParametersConfigurationEntityLoader({
      obs: splugParametersConfigurationRemoteControllerService.getByProcess(processId || ''),
      loaderService,
      methodKey: 'load'
    }),
    insertBatch: (insertDto) => splugParametersConfigurationEntityLoader({
      obs: splugParametersConfigurationRemoteControllerService.insertBatch(insertDto),
      loaderService,
      methodKey: 'insert'
    }),
    removeBatch: (ids) => splugParametersConfigurationEntityLoader({
      obs: splugParametersConfigurationRemoteControllerService.removeBatch(ids),
      loaderService,
      methodKey: 'remove'
    }),
  });

@Injectable()
export class RemoteSplugParametersConfigurationService extends RemoteCollectionService<ISplugParametersConfigurationDto, ISplugParametersConfiguration> {

  private readonly _destroy = new Subject<void>();

  readonly processIdSubject = this._signProcessWizardService.processIdSubject;
  get splugDefinitions() {
    return this._splugConfigService.splugDefinitions
  }


  constructor(
    private readonly _signProcessWizardService: SignProcessWizardService,
    private readonly _splugParametersConfigurationRemoteControllerService: SplugParametersConfigurationRemoteControllerService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly _splugConfigService: SplugConfigService,
    private readonly _translateService: TranslateService,
    private readonly _snackbarService: SnackbarService<DsgDeepTranslationKey>
  ) {

    super();

    const processId = _signProcessWizardService.processIdSubject.value;
    const splugParametersConfigurationStore = initRemoteStore(processId, this._splugParametersConfigurationRemoteControllerService, this._loaderService);

    super.setConfig({
      remoteStore: splugParametersConfigurationStore,
      map: (item) => ({
        ...item,
        sigTypeParam: !item ? undefined :
          Object.entries(item.sigTypeParam)
            .reduce<any>((total, [key, value]) => {
              total[key] = _splugConfigService.executeParser(item.sigTypeId, key, value);
              return total
            }, {}) as any
      }),
      unMap: (item) => ({
        ...item,
        sigTypeParam: !item ? undefined :
          Object.entries(item.sigTypeParam)
            .reduce<any>((total, [key, value]) => {
              total[key] = _splugConfigService.executeStringifer(item.sigTypeId, key, value);
              return total
            }, {}) as any
      }),
    })

    this.processIdSubject
      .pipe(takeUntil(this._destroy))
      .subscribe(processId => {

        this.remoteStore = initRemoteStore(processId, this._splugParametersConfigurationRemoteControllerService, _loaderService)
      })



    this.localCollectionStore.stateSubject
      .pipe(
        takeUntil(this._destroy),
        filter(Boolean)
      )
      .subscribe((data) => {
        this._signProcessWizardService.splugParametersConfigurationsSubject.next(data)

      })

    this.processIdSubject.pipe(
      filter(Boolean),
      takeUntil(this._destroy)
    )
      .subscribe(() => {
        this.load({ autoSync: true }).pipe(takeUntil(this._destroy)).subscribe()

      })
  }

  override insertBatch(insertJSONArray: ISplugParametersConfiguration<LSplug>[]) {
    return super.insertBatch(insertJSONArray)
      .pipe(
        tap((insertedUnmapped) => {
          const inserted = this.localCollectionStore.mapMany(insertedUnmapped);

          const localCollectionStore = this.localCollectionStore;
          const actorIds = inserted.reduce<Set<string>>((actorIds, current) => {
            actorIds.add(current.actorId);
            return actorIds;
          },
            new Set<string>()
          )

          const prevState = localCollectionStore.stateSubject.value.filter(({ actorId }) => !actorIds.has(actorId));

          localCollectionStore.stateSubject.next([
            ...prevState,
            ...inserted
          ])

        })
      )
  }

  loadByActivity(activityId: string, actorId?: string) {

    return this._splugParametersConfigurationRemoteControllerService.getByActivity(activityId)
      .pipe(
        tap(parameterConfigurations => {

          this.localCollectionStore.addMany(parameterConfigurations)
        }),
        tap(added => {
          if (!added.length) {
            return;
          }

          const translated = this._translateService.instant('ss-process-wizard.default-splug-configs-added' as DsgDeepTranslationKey);

          const splugTitles = added
            .map(({ sigTypeId }) => this.splugDefinitions.find(({ id }) => id === sigTypeId)?.title)
            .filter(Boolean)
            .join(', ')

          this._snackbarService.open({
            message: translated + ': ' + splugTitles as DsgDeepTranslationKey,
            type: 'info',
          })

        }),
      )
  }

  ngOnDestroy() {
    this._destroy.next();
    this._destroy.complete();
  }
}
